// The plan-limit dialog.
//
// Replaces red error strips at the top of the page. A strip reads as "you did
// something wrong"; hitting a plan ceiling is not an error, it's the product
// working as sold — so this explains the limit, says what Pro buys, and offers
// the one action worth taking. Same shape Moises uses for its own ceilings, in
// our palette rather than theirs. (The out-of-separations variant deliberately
// does NOT count down to the refill — owner's call, 2026-08-15: a countdown
// invites waiting rather than deciding.)
//
// The quota variant is shown BEFORE any work starts (see the pre-flight in
// StudioNext's `onCta`). Discovering a ceiling after sitting through a
// real-time recording is the failure this exists to prevent.
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { funnelEvent } from '../lib/funnel';
// Four ceilings render their own offer card, with the plans and checkout inside
// it — see FullSongUpsell.tsx: the 60s section cap ('length'), running out of
// separations ('quota'), and since 2026-09-19 Best quality ('quality') and the
// chord ceiling ('chords'). Every other reason still renders the shared card
// below, unchanged. Delete this import and the early returns in UpgradeModal to
// go back to the single-CTA card for all of them.
import { FullSongUpsell } from './FullSongUpsell';

// Which ceiling was hit, or — for 'remaining' — the once-a-day heads-up that
// you're approaching one. Each gets its own art, headline and explanation.
export type UpgradeReason = 'quota' | 'quality' | 'remaining' | 'metronome' | 'chords' | 'loopcap' | 'length';

interface Props {
  open: boolean;
  onClose: () => void;
  reason: UpgradeReason;
  /** Anonymous visitors can sign in for their own allowance; members upgrade. */
  anonymous?: boolean;
  dailyQuota: number;
  /** Separations left in the current window — only read by 'remaining'. */
  remaining?: number;
  /** ISO timestamp from /api/usage/me — when the current window expires. */
  resetsAt?: string | null;
  /** Saved loops allowed per song on free — only read by 'loopcap'. */
  loopCap?: number;
  /** Free section ceiling in seconds — only read by 'length', so its copy
   *  tracks FREE_SECTION_SECONDS instead of restating it. */
  freeSeconds?: number;
  /** Free chord ceiling in seconds — only read by 'chords' (FREE_CHORDS_SECONDS). */
  freeChordsSeconds?: number;
}

// "in about 3 hours" / "in 40 minutes". The quota is a ROLLING 24h window from
// first use (see the PHP: RL_FREE_WINDOW_SECS), NOT a midnight reset — so
// "tomorrow" or "daily" would be plainly wrong for someone whose first
// separation was at 9pm. Returns null when there's no usable timestamp, and the
// copy then names the window rather than inventing a time.
function untilReset(iso?: string | null): string | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (!Number.isFinite(ms) || ms <= 0) return null;
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `in ${Math.max(1, mins)} minute${mins === 1 ? '' : 's'}`;
  const hours = Math.round(mins / 60);
  return `in about ${hours} hour${hours === 1 ? '' : 's'}`;
}

function ClockArt() {
  return (
    <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3.5 2" />
    </svg>
  );
}

function SparkArt() {
  return (
    <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l2.2 5.4L20 10.5l-5.8 2.1L12 18l-2.2-5.4L4 10.5l5.8-2.1z" />
      <path d="M18.5 16.5l.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8z" />
    </svg>
  );
}

function StackArt() {
  return (
    <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 4h7l4 4v10a2 2 0 01-2 2H7a2 2 0 01-2-2V6a2 2 0 012-2z" />
      <path d="M14 4v4h4" />
      <path d="M9.5 16.5a1.5 1.5 0 103 0v-4l2.5.9" />
    </svg>
  );
}

function MetronomeArt() {
  return (
    <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9.6 3h4.8l4.1 18H5.5z" />
      <path d="M7.2 15h9.6" />
      <path d="M15.8 6.6L10.6 17" />
      <circle cx="16.2" cy="5.7" r="1.25" />
    </svg>
  );
}

function LoopArt() {
  return (
    <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 2l4 4-4 4" />
      <path d="M3 11V9a4 4 0 014-4h14" />
      <path d="M7 22l-4-4 4-4" />
      <path d="M21 13v2a4 4 0 01-4 4H3" />
    </svg>
  );
}

function ChordArt() {
  return (
    <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 6h14" />
      <path d="M5 6v13M9.7 6v13M14.3 6v13M19 6v13" />
      <path d="M5 10.3h14M5 14.6h14" />
      <circle cx="9.7" cy="12.4" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="14.3" cy="16.7" r="1.4" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function UpgradeModal({
  open, onClose, reason, anonymous = false, dailyQuota, remaining = 0, resetsAt, loopCap = 3,
  freeSeconds = 60, freeChordsSeconds = 30,
}: Props) {
  // Every ceiling that renders the shared card below reports here, once per
  // opening, into our own DB. Until 2026-09-05 these five walls (quality,
  // chords, metronome, loopcap, the heads-up) reported to GA4 only, so a
  // buyer's path through them was invisible from /admin. 'length', 'quota',
  // 'quality' and 'chords' are excluded: FullSongUpsell fires its own
  // open/click for those, and a second row here would double-count them.
  // Hooks sit above the early returns — React requires the same hook order on
  // every render.
  useEffect(() => {
    if (!open || reason === 'length' || reason === 'quota' || reason === 'quality' || reason === 'chords') return;
    funnelEvent('rl_upgrade_modal_open', { reason, tier: anonymous ? 'anonymous' : 'free' }, 'studio');
  }, [open, reason, anonymous]);
  if (!open) return null;
  // The two ceilings that actually stop someone mid-task get their own card —
  // plans and checkout in the dialog rather than a link to /pricing. Removing a
  // line here restores the shared card below for that reason; nothing else was
  // changed. 'remaining' is deliberately NOT here: it is a heads-up whose main
  // button is "Continue", not a wall, so an offer card would over-sell it.
  if (reason === 'length') {
    return <FullSongUpsell onClose={onClose} anonymous={anonymous} variant="length" freeSeconds={freeSeconds} />;
  }
  if (reason === 'quota') {
    return (
      <FullSongUpsell
        onClose={onClose}
        anonymous={anonymous}
        variant="quota"
        dailyQuota={dailyQuota}
      />
    );
  }
  // 2026-09-19: the two walls whose old card ended in "See plans" → /pricing.
  // Quality had the best click-through of any Studio wall and then sent people
  // on a detour; chords sold a Lifetime through the extension's priced modal.
  // Remove either line to get the shared card below back for that reason.
  if (reason === 'quality') {
    return <FullSongUpsell onClose={onClose} anonymous={anonymous} variant="quality" />;
  }
  if (reason === 'chords') {
    return <FullSongUpsell onClose={onClose} anonymous={anonymous} variant="chords" freeChordsSeconds={freeChordsSeconds} />;
  }
  const when = untilReset(resetsAt);
  // Unreachable like `length` below — the cast only stops TS narrowing the
  // union to nothing now that 'quota' returns early. The branch is left intact.
  const quota = (reason as UpgradeReason) === 'quota';
  const heads = reason === 'remaining';
  const metro = reason === 'metronome';
  // Unreachable like `quota` above once 'chords' returns early — same cast.
  const chords = (reason as UpgradeReason) === 'chords';
  // Unreachable while the early return above sends 'length' to FullSongUpsell —
  // the cast only stops TS narrowing the union to nothing. The whole 'length'
  // branch below is left intact on purpose: delete the early return and this
  // card renders it again exactly as it did before.
  const length = (reason as UpgradeReason) === 'length';
  const loops = reason === 'loopcap';

  return (
    <div
      className="rl-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="upsell-title"
      onClick={onClose}
    >
      {/* Stop the backdrop's dismiss from firing on clicks inside the card. */}
      <div className="sx-upsell" onClick={(e) => e.stopPropagation()}>
        <button className="sx-upsell-x" onClick={onClose} aria-label="Close">×</button>

        <div className="sx-upsell-art" aria-hidden>
          {loops ? <LoopArt /> : length ? <LoopArt /> : chords ? <ChordArt /> : metro ? <MetronomeArt /> : heads ? <StackArt /> : quota ? <ClockArt /> : <SparkArt />}
        </div>

        <div className="sx-upsell-body">
          {/* Says what happened, in the words someone would use themselves. */}
          <h3 id="upsell-title">
            {loops
              ? 'Room for more loops'
              : length
              ? 'Unlock the full song'
              : chords
              ? 'See the chords for the whole song'
              : metro
              ? 'Keep the click going'
              : heads
              ? `You have ${remaining} separation${remaining === 1 ? '' : 's'} left`
              : quota ? "You're out of separations" : 'Best quality is a Pro feature'}
          </h3>

          {length ? (
            <>
              <p>Free separations cover 60&nbsp;seconds of a song.</p>
              <p>
                Pro separates the whole track — every verse, every solo — and lets you
                export the stems.
              </p>
              <p className="sx-upsell-note">
                Looping is still free and unlimited: only separating is capped.
              </p>
            </>
          ) : loops ? (
            <>
              <p>Free plans keep {loopCap} saved loops per song.</p>
              <p>
                Pro removes the limit — save as many parts of a track as you want to
                practise, and switch between them as you go.
              </p>
              <p className="sx-upsell-note">
                Your markers are still on the track: delete a loop below to save this one instead.
              </p>
            </>
          ) : chords ? (
            <>
              <p>Free reads the chords for the first 30&nbsp;seconds of a song.</p>
              <p>
                Pro maps the whole track — every chord on the timeline, and the shapes
                to play them.
              </p>
              <p className="sx-upsell-note">Also unlimited separations, longer clips and higher-quality stems.</p>
            </>
          ) : metro ? (
            <>
              <p>
                Free gives you the click through the first minute of a song, running at
                the tempo you set.
              </p>
              <p>
                Pro keeps it going for the whole track and locks it to the song's own
                beats, so the click follows the music instead of you chasing it.
              </p>
              <p className="sx-upsell-note">Also unlimited separations, longer clips and higher-quality stems.</p>
            </>
          ) : heads ? (
            <>
              <p>
                Your free plan gives you {dailyQuota} every 24&nbsp;hours.
                {when ? <> This batch refills <b>{when}</b>.</> : null}
              </p>
              <p>Pro removes the limit — unlimited separations, longer clips and higher-quality stems.</p>
              <p className="sx-upsell-note">Looping a section doesn't count — that's always free.</p>
            </>
          ) : quota ? (
            <>
              {anonymous ? (
                <p>
                  You've used all {dailyQuota} free separations. Sign in to get your own
                  allowance, or go Pro for unlimited.
                </p>
              ) : (
                <p>Your free plan gives you {dailyQuota} every 24&nbsp;hours.</p>
              )}
              <p>Pro removes the limit — unlimited separations, longer clips and higher-quality stems.</p>
            </>
          ) : (
            <>
              {/* Describes the OUTCOME, never how it's done — naming the
                  engine in user-facing copy is a release blocker. */}
              <p>
                Best quality pulls the parts further apart — less bleed between instruments,
                which tells most on dense mixes and quiet passages.
              </p>
              <p>It's included with Pro, along with unlimited separations and longer clips.</p>
              <p className="sx-upsell-note">Standard stays free, and handles most tracks well.</p>
            </>
          )}

          <Link
            className="sx-upsell-cta"
            to={anonymous ? '/login' : '/pricing'}
            onClick={() => {
              // Same shape FullSongUpsell uses, so the two cards read as one
              // funnel in /admin: plan says where the click leads, not a SKU.
              funnelEvent('rl_upgrade_click', {
                source: 'upgrade_modal', context: reason, plan: anonymous ? 'sign_in' : 'see_plans',
              }, 'studio');
              onClose();
            }}
          >
            {anonymous ? 'Sign in' : 'Upgrade Plan'}
          </Link>
          {/* The heads-up interrupts someone who came here to work, so its
              dismiss is the positive action, not a shrug. */}
          <button className="sx-upsell-dismiss" onClick={onClose}>
            {heads ? 'Continue' : 'Maybe later'}
          </button>
        </div>
      </div>
    </div>
  );
}
